import React, { FC, HTMLAttributes, useCallback, useState } from 'react';
// import classNames from 'classnames';
import { Badge, Plan, PlanBadge, StatusBadge, Tooltip, UsageBadge } from 'components/ui';
import routesByName from 'constants/routes';
import { Link, useHistory } from 'react-router-dom';
import { FieldsInfo, Widget } from 'components';
import { t } from 'utils';
import appConfig from 'constants/appConfig';

import styles from './AppCard.module.scss';
import { AppStatus, TooltipPositions } from '../AppSettings/constants';

export interface IAppCard extends HTMLAttributes<HTMLDivElement> {
  id: string;
  name: string;
  imageUrl: string;
  version: string;
  status: string;
  isPublic: boolean;
  installmentsCount: number;
  plan: Plan;
  categories: string[];
  index: number;
  allowedCountries: Array<{ hasPayment: boolean }>;
}

const AppCard: FC<IAppCard> = (props) => {
  const {
    id,
    name,
    imageUrl,
    version,
    isPublic,
    allowedCountries,
    installmentsCount,
    plan,
    status,
    categories,
    index,
  } = props;
  const history = useHistory();
  const rootClassNames = [styles.root];
  const [isShowTooltip, setIsShowTooltip] = useState<boolean>(false);

  if (status === AppStatus.BLOCKED) {
    rootClassNames.push(styles.blocked);
  }

  const handleAppCardClick = useCallback(() => {
    if (status === AppStatus.BLOCKED) return;
    history.push(routesByName.apps.detail.index(id));
  }, [history, id, status]);
  const isPaid = allowedCountries.reduce(
    (isCountryPaid: boolean, country: { hasPayment: boolean }) => {
      if (isCountryPaid) return true;
      return country.hasPayment;
    },
    false
  );

  const toggleTooltip = (isShow: boolean) => () => {
    if (status === AppStatus.BLOCKED) {
      setIsShowTooltip(isShow);
    }
  };

  return (
    <div
      className={rootClassNames.join(' ')}
      onClick={handleAppCardClick}
      role='presentation'
      onMouseEnter={toggleTooltip(true)}
      onMouseLeave={toggleTooltip(false)}
    >
      <div className={styles.head}>
        <div className={styles.heading}>
          <img
            className={styles.logo}
            src={imageUrl}
            alt={`${name} app logo`}
            width='90'
            height='45'
          />
          <div className={styles.titleDescription}>
            <h3 className={styles.title}>
              {name} <span className={styles.version}>v.{version}</span>
            </h3>
            <p className={styles.description}>
              {categories?.map((category) => (
                <span className={styles.category} key={category}>
                  {t(`category.${category}`)}
                </span>
              ))}
            </p>
          </div>
        </div>
        <div className={styles.info}>
          <div className={styles.badgesRow}>
            {plan && <PlanBadge className={styles.badge} plan={plan} />}
            <UsageBadge className={styles.badge} isPublic={isPublic} />
            {isPaid && <Badge rootClassName='paid' icon='far fa-money-bill' />}
          </div>
          <Link className={styles.detailLink} to={routesByName.apps.detail.index(id)}>
            {t('see.app.details')}
          </Link>
        </div>
      </div>
      <div className={styles.main}>
        <div className={styles.mainInfo}>
          <FieldsInfo title={`${appConfig.environment || ''}.environment`} id={id} />
        </div>
        <div className={styles.mainStatistics}>
          <StatusBadge className={styles.status} status={status} />
          <Widget data={installmentsCount} title='installations' />
          <Widget data='0' title='active.users' />
        </div>
      </div>

      {status === AppStatus.BLOCKED && (
        <Tooltip
          className='block__tooltip'
          showTooltip={isShowTooltip}
          position={index % 2 !== 0 ? TooltipPositions.LEFT : TooltipPositions.RIGHT}
        >
          <div>{t('app.details.block.tooltip')}</div>
        </Tooltip>
      )}
    </div>
  );
};

export default AppCard;
